import AppLayout from "@/components/AppLayout";
import PageTitle from "@/components/PageTitle";
import { plan_bottom_up_by_day_index_path, plan_bottom_up_by_month_index_path } from "@/lib/routes";
import { useMarket } from "@/lib/useMarket";
import PlanHeader from "../components/PlanHeader";
import PlanNavRow from "../components/PlanNavRow";
import type { PlanSwitcherData } from "../TopDown/components/PlanSwitcher";

interface EmptyStatePlan {
  name: string;
  org_node_name: string | null;
  revenue_target: number;
  currency: string | null;
  begin_date: string;
  end_date: string;
}

// Why the page can't render: no session plan selected, a draft-only plan
// (no published version), or a plan the viewer can't open.
export type BlockedReason = "no_plan" | "draft" | "permission";

interface Props {
  title: string;
  page: "by_month" | "by_day";
  plan_switcher: PlanSwitcherData;
  // Present when a session plan exists but is blocked — the header still
  // names it.
  plan?: EmptyStatePlan | null;
  blocked?: BlockedReason;
}

const MESSAGES: Record<BlockedReason, string> = {
  no_plan: "No published top-down plan selected. Select a published plan to view this page.",
  draft: "The current specified Top Down plan is not published yet. It needs to be published before viewing this page.",
  permission: "You don't have permission to open this plan's bottom-up planning. Ask an admin to grant it to your role.",
};

// Empty state for the session-plan bottom-up pages. The switcher stays so
// they can pick another plan without leaving the page.
export default function NoCurrentPlan({ title, page, plan_switcher, plan = null, blocked = "no_plan" }: Props) {
  const market = useMarket();
  const redirectPath = page === "by_day"
    ? plan_bottom_up_by_day_index_path(market.slug)
    : plan_bottom_up_by_month_index_path(market.slug);
  const message = MESSAGES[blocked];
  return (
    <AppLayout
      header={
        plan ? (
          <PlanHeader
            name={plan.name}
            orgNodeName={plan.org_node_name}
            revenueTarget={plan.revenue_target}
            currency={plan.currency}
            beginDate={plan.begin_date}
            endDate={plan.end_date}
          />
        ) : (
          <PageTitle title={title} />
        )
      }
    >
      <PlanNavRow planSwitcher={plan_switcher} redirectPath={redirectPath} className="mb-4" />
      <div
        style={{ background: "var(--background)", borderColor: "var(--border)" }}
        className="border rounded-lg px-3 py-2.5 text-[13px] text-foreground text-center"
      >
        {message}
      </div>
    </AppLayout>
  );
}
